import React, { useMemo, useState, useCallback, useEffect } from "react";
import { exportPDF } from "./services/pdfExportService";
import { BOARD_CONFIG } from "./constants/boardConfig";
import { getWeekStart, formatDate } from "./utils/dateUtils";
import { extractBoardId } from "./utils/contextUtils";
import { debounce } from "./utils/debounceThrottle";
import { useProperties } from "./hooks/useProperties";
import { usePersonColumn } from "./hooks/usePersonColumn";
import { useTasks } from "./hooks/useTasks";
import { useTaskActions } from "./hooks/useTaskActions";
import Header from "./components/layout/Header";
import DailyView from "./components/views/DailyView";
import WeeklyView from "./components/views/WeeklyView";
import TaskPopup from "./components/modals/TaskPopup";
import NewTaskPopup from "./components/modals/NewTaskPopup";
import LoadingSpinner from "./components/common/LoadingSpinner";
import PropertyProfilePanel from "./components/panels/PropertyProfilePanel";
import ToastContainer from "./components/common/ToastContainer";
import { useToast } from "./hooks/useToast";

const PropertyDailyTodoList = ({ context, onBoardChange }) => {
  // Determine default view type based on screen size
  // Desktop (>= 768px): weekly, Mobile (< 768px): daily
  const getDefaultViewType = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768 ? "weekly" : "daily";
    }
    return "daily"; // Default fallback
  };

  const [viewType, setViewType] = useState(getDefaultViewType); // "daily" or "weekly"
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedPerson, setSelectedPerson] = useState("all"); // "all" or person ID
  const [isPropertyPanelOpen, setIsPropertyPanelOpen] = useState(false);

  // Debounce person filter changes to prevent rapid data processing (300ms delay)
  const [debouncedSelectedPerson, setDebouncedSelectedPerson] = useState("all");

  // Use useMemo to create a stable debounced function reference
  const debouncedSetSelectedPerson = useMemo(
    () => debounce((value) => {
      setDebouncedSelectedPerson(value);
    }, 300),
    [] // Empty deps - debounce function should be stable
  );

  // Update debounced value when selectedPerson changes
  useEffect(() => {
    debouncedSetSelectedPerson(selectedPerson);
    // Also update immediately if switching to "all" for better UX
    if (selectedPerson === "all") {
      setDebouncedSelectedPerson("all");
    }
  }, [selectedPerson, debouncedSetSelectedPerson]);

  const boardId = useMemo(() => context?.boardId || extractBoardId(context), [context]);
  const config = boardId ? BOARD_CONFIG[boardId] : null;

  // Custom hooks - these now read/write from MondayContext
  const { propertyKeyMap, availableProperties } = useProperties();
  const { personColumnId, personColumnIdMap, availablePeople } = usePersonColumn();

  const {
    loading,
    error,
    tasksByProperty,
    setTasksByProperty,
    propertyNameToIdMap,
    handleRefresh,
  } = useTasks({
    viewType,
    currentDate,
    currentWeekStart,
    selectedPerson: debouncedSelectedPerson, // Use debounced value to prevent rapid processing
    personColumnId,
    personColumnIdMap,
    config,
    boardId,
  });

  // Toast notifications
  const toast = useToast();

  const taskActions = useTaskActions({
    viewType,
    boardId,
    config,
    handleRefresh,
    personColumnId,
    toast, // Pass toast to taskActions
    currentDate,
    currentWeekStart,
  });

  // Navigation handlers - memoized with useCallback to prevent unnecessary re-renders
  const handlePrevious = useCallback(() => {
            if (viewType === "daily") {
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
        return newDate;
      });
    } else {
      setCurrentWeekStart((prev) => {
        const newWeek = new Date(prev);
      newWeek.setDate(newWeek.getDate() - 7);
        return newWeek;
      });
    }
  }, [viewType]);

  const handleNext = useCallback(() => {
    if (viewType === "daily") {
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
        return newDate;
      });
    } else {
      setCurrentWeekStart((prev) => {
        const newWeek = new Date(prev);
      newWeek.setDate(newWeek.getDate() + 7);
        return newWeek;
      });
    }
  }, [viewType]);

  const handleToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    setCurrentWeekStart(getWeekStart(today));
  }, []);

  const handleExportPDF = useCallback(() => {
    exportPDF({
      viewType,
      currentDate,
      currentWeekStart,
      tasksByProperty,
      selectedPerson,
      availablePeople,
      propertyNameToIdMap,
      propertyKeyMap,
    });
  }, [viewType, currentDate, currentWeekStart, tasksByProperty, selectedPerson, availablePeople, propertyNameToIdMap, propertyKeyMap]);

  // Extract task action handlers and state from the hook
  const {
    selectedTask,
    showPopup,
    reportText,
    setReportText,
    reportImages,
    sendingReport,
    confirmingTask,
    waterReading,
    setWaterReading,
    handleWaterPhotoUpload,
    removeWaterPhoto,
    gasReading,
    setGasReading,
    handleGasPhotoUpload,
    removeGasPhoto,
    electricityReading,
    setElectricityReading,
    readingDates,
    savingReading,
    handleSaveElectricityReading,
    handleSaveWaterReading,
    handleSaveGasReading,
    taskImages,
    setTaskImages,
    handleTaskPhotoUpload,
    removeTaskPhoto,
    taskComments,
    setTaskComments,
    showNewTaskPopup,
    setShowNewTaskPopup,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskDate,
    setNewTaskDate,
    newTaskPerson,
    setNewTaskPerson,
    newTaskProperty,
    setNewTaskProperty,
    newTaskPropertySearch,
    setNewTaskPropertySearch,
    newTaskTimeTracking,
    setNewTaskTimeTracking,
    timeSpent,
    setTimeSpent,
    creatingTask,
    completedMeterReadingData,
    completedTaskData,
    extras,
    setExtras,
    savingExtras,
    handleSaveExtras,
    isEditingTask,
    setIsEditingTask,
    editTaskName,
    setEditTaskName,
    editTaskDescription,
    setEditTaskDescription,
    editTaskDate,
    setEditTaskDate,
    editTaskPerson,
    setEditTaskPerson,
    editTaskTimeTracking,
    setEditTaskTimeTracking,
    savingTask,
    deletingTask,
    handleSaveTask,
    handleDeleteTask,
    propertyDetails,
    handleTaskClick,
    handleClosePopup,
    handleImageUpload,
    removeImage,
    handleCloseNewTaskPopup,
    handleToggle,
    handleConfirmTask,
    handleSendReport,
    handleCreateTask,
    imageProgress,
    showConfirmedState,
    confirmationProgress,
  } = taskActions;

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (Rules of Hooks)
  // Prepare view data - handle cases where tasksByProperty might be undefined
  const viewData = useMemo(() => {
    if (!tasksByProperty || Object.keys(tasksByProperty).length === 0) {
      return { propertyNames: [], dayNames: [], hasTasks: false };
    }

    if (viewType === "daily") {
      const propertyNames = Object.keys(tasksByProperty).sort((a, b) => a.localeCompare(b));
      const hasTasks = propertyNames.some((propName) => {
        const data = tasksByProperty[propName];
        return Array.isArray(data) && data.length > 0;
      });
      return { propertyNames, dayNames: [], hasTasks };
    } else {
      const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const hasTasks = dayOrder.some((day) => {
        const dayData = tasksByProperty[day];
        return dayData && typeof dayData === "object" && Object.keys(dayData).some((prop) => dayData[prop]?.length > 0);
      });
      return { propertyNames: [], dayNames: dayOrder, hasTasks };
    }
  }, [viewType, tasksByProperty]);

  const { propertyNames, dayNames, hasTasks } = viewData;

  // Memoize props for DailyView to prevent unnecessary re-renders - MUST be called before early returns
  const dailyViewProps = useMemo(() => ({
    propertyNames,
    tasksByProperty,
    propertyNameToIdMap,
    propertyKeyMap,
    handleTaskClick,
    handleToggle,
  }), [propertyNames, tasksByProperty, propertyNameToIdMap, propertyKeyMap, handleTaskClick, handleToggle]);

  // Memoize props for WeeklyView to prevent unnecessary re-renders - MUST be called before early returns
  const weeklyViewProps = useMemo(() => ({
    dayNames,
    tasksByProperty,
    currentWeekStart,
    propertyNameToIdMap,
    propertyKeyMap,
    handleTaskClick,
    handleToggle,
  }), [dayNames, tasksByProperty, currentWeekStart, propertyNameToIdMap, propertyKeyMap, handleTaskClick, handleToggle]);

  // Panel toggle handler
  const handlePropertyPanelToggle = useCallback(() => {
    setIsPropertyPanelOpen(prev => !prev);
  }, []);

  // Memoize props for Header to prevent unnecessary re-renders - MUST be called before early returns
  const headerProps = useMemo(() => ({
    viewType,
    setViewType,
    handleRefresh,
    handlePrevious,
    handleNext,
    handleToday,
    currentDate,
    currentWeekStart,
    personColumnId,
    personColumnIdMap,
    selectedPerson,
    setSelectedPerson,
    availablePeople,
    setShowNewTaskPopup,
    handleExportPDF,
    onBoardChange,
    onPropertyPanelToggle: handlePropertyPanelToggle,
  }), [viewType, setViewType, handleRefresh, handlePrevious, handleNext, handleToday, currentDate, currentWeekStart, personColumnId, personColumnIdMap, selectedPerson, setSelectedPerson, availablePeople, setShowNewTaskPopup, handleExportPDF, onBoardChange, handlePropertyPanelToggle]);

  // Memoize props for TaskPopup to prevent unnecessary re-renders - MUST be called before early returns
  const taskPopupProps = useMemo(() => ({
    task: selectedTask,
    onClose: handleClosePopup,
    reportText,
    setReportText,
    reportImages,
    handleImageUpload,
    removeImage,
    handleSendReport,
    sendingReport,
    handleConfirmTask,
    confirmingTask,
    waterReading,
    setWaterReading,
    handleWaterPhotoUpload,
    removeWaterPhoto,
    gasReading,
    setGasReading,
    handleGasPhotoUpload,
    removeGasPhoto,
    electricityReading,
    setElectricityReading,
    readingDates,
    savingReading,
    handleSaveElectricityReading,
    handleSaveWaterReading,
    handleSaveGasReading,
    taskImages,
    setTaskImages,
    handleTaskPhotoUpload,
    removeTaskPhoto,
    taskComments,
    setTaskComments,
    timeSpent,
    setTimeSpent,
    imageProgress,
    showConfirmedState,
    confirmationProgress,
    completedMeterReadingData,
    completedTaskData,
    extras,
    setExtras,
    handleSaveExtras,
    savingExtras,
    isEditingTask,
    setIsEditingTask,
    editTaskName,
    setEditTaskName,
    editTaskDescription,
    setEditTaskDescription,
    editTaskDate,
    setEditTaskDate,
    editTaskPerson,
    setEditTaskPerson,
    editTaskTimeTracking,
    setEditTaskTimeTracking,
    savingTask,
    deletingTask,
    handleSaveTask,
    handleDeleteTask,
    availablePeople,
    propertyDetails,
  }), [selectedTask, handleClosePopup, reportText, setReportText, reportImages, handleImageUpload, removeImage, handleSendReport, sendingReport, handleConfirmTask, confirmingTask, waterReading, setWaterReading, handleWaterPhotoUpload, removeWaterPhoto, gasReading, setGasReading, handleGasPhotoUpload, removeGasPhoto, electricityReading, setElectricityReading, readingDates, savingReading, handleSaveElectricityReading, handleSaveWaterReading, handleSaveGasReading, taskImages, setTaskImages, handleTaskPhotoUpload, removeTaskPhoto, taskComments, setTaskComments, timeSpent, setTimeSpent, imageProgress, showConfirmedState, confirmationProgress, completedMeterReadingData, extras, setExtras, handleSaveExtras, savingExtras, isEditingTask, setIsEditingTask, editTaskName, setEditTaskName, editTaskDescription, setEditTaskDescription, editTaskDate, setEditTaskDate, editTaskPerson, setEditTaskPerson, editTaskTimeTracking, setEditTaskTimeTracking, savingTask, deletingTask, handleSaveTask, handleDeleteTask, availablePeople, propertyDetails]);

  // Memoize props for NewTaskPopup to prevent unnecessary re-renders - MUST be called before early returns
  const newTaskPopupProps = useMemo(() => ({
    onClose: handleCloseNewTaskPopup,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskDate,
    setNewTaskDate,
    newTaskPerson,
    setNewTaskPerson,
    newTaskProperty,
    setNewTaskProperty,
    newTaskPropertySearch,
    setNewTaskPropertySearch,
    newTaskTimeTracking,
    setNewTaskTimeTracking,
    availablePeople,
    availableProperties,
    personColumnId,
    personColumnIdMap,
    handleCreateTask,
    creatingTask,
  }), [handleCloseNewTaskPopup, newTaskTitle, setNewTaskTitle, newTaskDescription, setNewTaskDescription, newTaskDate, setNewTaskDate, newTaskPerson, setNewTaskPerson, newTaskProperty, setNewTaskProperty, newTaskPropertySearch, setNewTaskPropertySearch, newTaskTimeTracking, setNewTaskTimeTracking, availablePeople, availableProperties, personColumnId, personColumnIdMap, handleCreateTask, creatingTask]);

  // NOW early returns can happen - all hooks have been called
  if (!context) {
    return (
      <div style={{ padding: 24 }}>
        <div>Loading monday context…</div>
        <div style={{ marginTop: 16, fontSize: 12, color: "#676879" }}>
          If this persists, make sure:
          <ul style={{ marginTop: 8 }}>
            <li>You're accessing this as a Board View in monday.com</li>
            <li>The app is properly configured in the Developers section</li>
            <li>You've added the Board View feature to your board</li>
          </ul>
        </div>
      </div>
    );
  }

  if (!boardId || !config) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 8 }}>
          This view is not attached to one of the configured boards (TASKS,
          Weekly Checks, Maintenance, Meter Readings, Reports).
        </div>
        {context && (
          <details style={{ marginTop: 16, fontSize: 12, color: "#676879" }}>
            <summary>Debug: Context structure</summary>
            <pre style={{ background: "#f4f5f8", padding: 8, overflow: "auto" }}>
              {JSON.stringify(context, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Finding tasks for Gert to do..." />;
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: "#d83a52" }}>
        Error loading tasks: {error}
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Property Profile Panel - only visible in weekly view when open */}
      {viewType === "weekly" && isPropertyPanelOpen && (
        <PropertyProfilePanel
          isOpen={isPropertyPanelOpen}
          onClose={() => setIsPropertyPanelOpen(false)}
          availableProperties={availableProperties}
          propertyKeyMap={propertyKeyMap}
          propertyNameToIdMap={propertyNameToIdMap}
          onNewTask={(propertyId, propertyName) => {
            setNewTaskProperty(propertyId);
            setNewTaskPropertySearch(propertyName);
            setShowNewTaskPopup(true);
          }}
          onTaskClick={handleTaskClick}
          toast={toast}
        />
      )}

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Header {...headerProps} />

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px 2px",
            paddingBottom: viewType === "daily" ? "80px" : "8px",
          }}
        >
          {!hasTasks && (
            <div style={{ color: "#676879", textAlign: "center", padding: "40px 0" }}>
              No tasks scheduled for {viewType === "daily" ? formatDate(currentDate) : "this week"} on this board.
            </div>
          )}

          {viewType === "daily" ? (
            <DailyView {...dailyViewProps} />
          ) : (
            <WeeklyView {...weeklyViewProps} />
          )}
        </div>

        {showPopup && selectedTask && (
          <TaskPopup {...taskPopupProps} />
        )}

        {showNewTaskPopup && (
          <NewTaskPopup {...newTaskPopupProps} />
        )}
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
    </div>
  );
};

export default PropertyDailyTodoList;
